import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const FinancePayroll = () => {
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [openDetails, setOpenDetails] = useState(false);
  const [openReject, setOpenReject] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Menu states
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Months list
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  // ========== GET AUTH TOKEN ==========
  const getAuthToken = () => {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  };

  const getHeaders = () => {
    const token = getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // ========== FETCH BATCHES ==========
  const fetchBatches = async () => {
    setLoading(true);
    setMessage(null);

    try {
      let url = `${API_URL}/api/finance/payroll/batches`;
      const params = new URLSearchParams();
      
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (monthFilter) params.append('month', monthFilter);
      if (yearFilter) params.append('year', yearFilter);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, { headers: getHeaders() });
      setBatches(response.data.data);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to fetch batches'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, [statusFilter, monthFilter, yearFilter]);

  // ========== MENU HANDLERS ==========
  const handleMenuOpen = (event, record) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRecord(null);
  };

  // ========== VIEW BATCH DETAILS ==========
  const handleViewBatch = async (batchId) => {
    setLoading(true);
    setMessage(null);
    handleMenuClose();

    try {
      const response = await axios.get(
        `${API_URL}/api/finance/payroll/batch/${batchId}`,
        { headers: getHeaders() }
      );
      setSelectedBatch(response.data.data);
      setOpenDetails(true);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to fetch batch details'
      });
    } finally {
      setLoading(false);
    }
  };

  // ========== APPROVE BATCH ==========
  const handleApprove = async (batchId) => {
    if (!window.confirm('Are you sure you want to approve this payroll batch?')) {
      return;
    }

    setActionLoading(true);
    setMessage(null);
    handleMenuClose();

    try {
      const response = await axios.post(
        `${API_URL}/api/finance/payroll/batch/${batchId}/approve`,
        {},
        { headers: getHeaders() }
      );
      
      setMessage({
        type: 'success',
        text: response.data.message
      });
      fetchBatches();
      setOpenDetails(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to approve batch'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // ========== REJECT BATCH ==========
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setMessage({
        type: 'error',
        text: 'Please provide a rejection reason'
      });
      return;
    }

    setActionLoading(true);
    setMessage(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/finance/payroll/batch/${selectedBatch?.batch?.id}/reject`,
        { rejection_reason: rejectionReason },
        { headers: getHeaders() }
      );
      
      setMessage({
        type: 'success',
        text: response.data.message
      });
      setOpenReject(false);
      setRejectionReason('');
      fetchBatches();
      setOpenDetails(false);
      handleMenuClose();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to reject batch'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // ========== GET STATUS COLOR ==========
  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted':
      case 'reviewing':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // ========== FORMAT DATE ==========
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ========== RENDER MENU ITEMS ==========
  const renderMenuItems = (record) => {
    const items = [];

    // View Details - Always available
    items.push(
      <MenuItem key="view" onClick={() => handleViewBatch(record.id)}>
        <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary="View Details" />
      </MenuItem>
    );

    // Approve/Reject only for submitted/reviewing status
    if (record.status === 'submitted' || record.status === 'reviewing') {
      items.push(<Divider key="divider1" />);
      items.push(
        <MenuItem key="approve" onClick={() => handleApprove(record.id)}>
          <ListItemIcon><ApproveIcon color="success" fontSize="small" /></ListItemIcon>
          <ListItemText primary="Approve" sx={{ color: 'success.main' }} />
        </MenuItem>
      );
      items.push(
        <MenuItem key="reject" onClick={() => {
          // Fetch batch details first then open reject dialog
          handleViewBatch(record.id);
          setTimeout(() => setOpenReject(true), 500);
          handleMenuClose();
        }}>
          <ListItemIcon><RejectIcon color="error" fontSize="small" /></ListItemIcon>
          <ListItemText primary="Reject" sx={{ color: 'error.main' }} />
        </MenuItem>
      );
    }

    return items;
  };

  // ========== RENDER BATCH LIST ==========
  const renderBatchList = () => {
    if (batches.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No payroll batches found
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Batch Code</strong></TableCell>
              <TableCell><strong>Month</strong></TableCell>
              <TableCell><strong>Year</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Employees</strong></TableCell>
              <TableCell><strong>LOP Days</strong></TableCell>
              <TableCell><strong>OT Hours</strong></TableCell>
              <TableCell><strong>Advance (₹)</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.map((batch) => (
              <TableRow key={batch.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {batch.batch_code}
                  </Typography>
                </TableCell>
                <TableCell>{batch.month}</TableCell>
                <TableCell>{batch.year}</TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(batch.status)}
                    color={getStatusColor(batch.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{batch.total_employees}</TableCell>
                <TableCell>{batch.total_lop_days}</TableCell>
                <TableCell>{batch.total_ot_hours}</TableCell>
                <TableCell>₹{batch.total_advance?.toLocaleString() || 0}</TableCell>
                <TableCell align="center">
                  <IconButton
                    onClick={(event) => handleMenuOpen(event, batch)}
                    size="small"
                    disabled={loading}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ========== RENDER DETAILS DIALOG ==========
  const renderDetailsDialog = () => {
    if (!selectedBatch) return null;

    const { batch, employees, summary } = selectedBatch;

    return (
      <Dialog
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="h6">
                Batch: {batch.batch_code}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {batch.month} {batch.year}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip
                label={getStatusLabel(batch.status)}
                color={getStatusColor(batch.status)}
              />
              {batch.submitted_at && (
                <Typography variant="caption" color="textSecondary">
                  Submitted: {formatDate(batch.submitted_at)}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="caption" color="textSecondary">
                  Total Employees
                </Typography>
                <Typography variant="h6">{summary.total_employees}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                <Typography variant="caption" color="textSecondary">
                  Total LOP Days
                </Typography>
                <Typography variant="h6">{summary.total_lop_days}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                <Typography variant="caption" color="textSecondary">
                  Total OT Hours
                </Typography>
                <Typography variant="h6">{summary.total_ot_hours}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                <Typography variant="caption" color="textSecondary">
                  Total Reimbursements
                </Typography>
                <Typography variant="h6">₹{summary.total_reimbursements?.toLocaleString() || 0}</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Employee Table */}
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Employee Details (As submitted by HR)
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
            HR has verified and adjusted attendance if needed.
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell><strong>Employee ID</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Worked Days</strong></TableCell>
                  <TableCell><strong>LOP Days</strong></TableCell>
                  <TableCell><strong>OT Hours</strong></TableCell>
                  <TableCell><strong>OT Rate</strong></TableCell>
                  <TableCell><strong>Advance (₹)</strong></TableCell>
                  <TableCell><strong>Reimbursements (₹)</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((emp) => (
                  <TableRow key={emp.employee_id} hover>
                    <TableCell>{emp.employee_id}</TableCell>
                    <TableCell>{emp.name}</TableCell>
                    <TableCell>{emp.worked_days}</TableCell>
                    <TableCell>{emp.lop_days}</TableCell>
                    <TableCell>{emp.ot_hours}</TableCell>
                    <TableCell>₹{emp.ot_rate}</TableCell>
                    <TableCell>₹{emp.advance_salary}</TableCell>
                    <TableCell>₹{emp.reimbursements}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Rejection Reason if rejected */}
          {batch.rejection_reason && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <strong>Rejection Reason:</strong> {batch.rejection_reason}
            </Alert>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Note:</strong> This shows HR submitted data. Finance team to verify and approve/reject.
          </Alert>
        </DialogContent>

        <DialogActions>
          {/* Show approve/reject buttons only if submitted */}
          {batch.status === 'submitted' && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => handleApprove(batch.id)}
                disabled={actionLoading}
              >
                {actionLoading ? <CircularProgress size={24} /> : 'Approve'}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => {
                  setOpenReject(true);
                }}
                disabled={actionLoading}
              >
                Reject
              </Button>
            </>
          )}

          {batch.status === 'approved' && (
            <Alert severity="success" sx={{ flex: 1 }}>
              ✅ This batch has been approved on {formatDate(batch.approved_at)}
            </Alert>
          )}

          {batch.status === 'rejected' && (
            <Alert severity="error" sx={{ flex: 1 }}>
              ❌ This batch has been rejected
            </Alert>
          )}

          <Button onClick={() => setOpenDetails(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ========== REJECT DIALOG ==========
  const renderRejectDialog = () => {
    return (
      <Dialog open={openReject} onClose={() => setOpenReject(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <RejectIcon color="error" />
            Reject Batch
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
            Please provide a reason for rejecting this batch. This will be sent to HR.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason *"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter detailed reason for rejection..."
            required
            error={!rejectionReason.trim()}
            helperText={!rejectionReason.trim() ? 'Rejection reason is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReject(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading || !rejectionReason.trim()}
            startIcon={actionLoading ? <CircularProgress size={20} /> : null}
          >
            {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ========== MAIN RENDER ==========
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Payroll Approval
        </Typography>
        <Chip
          label={`${batches.length} Batches`}
          color="primary"
          variant="outlined"
        />
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="submitted">Submitted</MenuItem>
                  <MenuItem value="reviewing">Reviewing</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                  label="Month"
                >
                  <MenuItem value="">All Months</MenuItem>
                  {months.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  label="Year"
                >
                  <MenuItem value="">All Years</MenuItem>
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchBatches}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Refresh'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Batch List */}
      <Card>
        <CardContent>
          {loading && !openDetails ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            renderBatchList()
          )}
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: 2,
            boxShadow: (theme) => theme.shadows[3],
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
            },
          },
        }}
      >
        {selectedRecord && renderMenuItems(selectedRecord)}
      </Menu>

      {/* Details Dialog */}
      {renderDetailsDialog()}

      {/* Reject Dialog */}
      {renderRejectDialog()}
    </Container>
  );
};

export default FinancePayroll;